import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";
import { useAccessToken } from "../useAccessToken";

export const resourceKeys = {
  resources: () => ["resources"],
  projectResourceAllocations: () => [...resourceKeys.resources(), "projects"],
  personResourceAllocations: () => [...resourceKeys.resources(), "people"],
};

export type ProjectResourceAllocationData = {
  projectId: string;
  project: {
    name: string;
    startDate?: string;
    endDate?: string;
    budgetedTime?: number;
  };
  users: {
    userId: string;
    user: {
      name: string;
      email: string;
    };
    allocations: ResourceAllocationData[];
  }[];
  roles: {
    role: {
      name: string
    }
    roleId: string;
    allocations: ResourceAllocationData[];
  }[];
};

type ResourceAllocationData = {
  allocationGroupId: string;
  phaseId: string;
  userId?: string 
  roleId?: string
  startDate: string;
  endDate: string;
  timePerWeek: number;
  allocationType: "soft" | "hard";
};

export const useGetProjectResourceAllocations = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    resourceKeys.projectResourceAllocations(),
    async (): Promise<ProjectResourceAllocationData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/resourceAllocations`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            groupBy: "project",
            startDate: new Date(2000, 0, 1).toISOString(),
            endDate: new Date(2030, 11, 31).toISOString(),
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useCreateResourceAllocation = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (resourceAllocation: {
      userId?: string;
      roleId?: string
      phaseId: string;
      startDate: string;
      endDate: string;
      timePerWeek: number;
      allocationType: "soft" | "hard";
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/resourceAllocation`,
        resourceAllocation,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(resourceKeys.resources());
    },
  });
};

export const useUpdateResourceAllocation = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (resourceAllocation: {
      allocationGroupId: string;
      startDate?: string;
      endDate?: string;
      timePerWeek?: number;
      allocationType?: "soft" | "hard";
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateResourceAllocation`,
        resourceAllocation,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating resource allocation", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(resourceKeys.resources());
    },
  });
};

export const useDeleteResourceAllocation = () => {
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (resourceAllocation: {
      allocationGroupId: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/resourceAllocation`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: resourceAllocation,
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error deleting resource allocation", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(resourceKeys.resources());
    },
  });
};
